import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { cn } from '#app/utils/misc'
import { siteContent } from '#app/utils/site/content'
import {
	AUTH_IMAGE,
	type Audience,
	SITE_LINKS,
} from '#app/utils/site/site-links'
import { Wordmark } from '../Wordmark'
import { BigHeading } from '../primitives'

const FOCUS_RING =
	'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent'

/** The photo panel is `display: none` below this width (see the aside). */
const PHOTO_MEDIA = '(min-width: 601px)'

/** A transparent 1x1 GIF: what phones get instead of the photo. */
const BLANK_PIXEL =
	'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

/** Where the photo sits inside its panel, per audience (the reference). */
const PHOTO_POSITION: Record<Audience, string> = {
	tenant: 'object-[58%_30%]',
	landlord: 'object-[50%_40%]',
}

/** The reference's dark top gradients so the white wordmark reads. */
const PHOTO_SHADE: Record<Audience, string> = {
	tenant:
		'bg-[linear-gradient(180deg,rgba(30,22,40,.38),rgba(30,22,40,0)_42%)]',
	landlord:
		'bg-[linear-gradient(180deg,rgba(0,0,0,.35),rgba(0,0,0,0)_40%),linear-gradient(0deg,rgba(0,0,0,.25),rgba(0,0,0,0)_30%)]',
}

/**
 * The split auth screen: a sticky photo panel with the wordmark and the
 * audience tagline, and a form column centred beside it. Pages wrap their
 * form in it and export `links = siteLinks()` so the site stylesheet loads.
 *
 * Layout by width (mobile first):
 * - up to 600px: no photo; the ink wordmark at the top with safe-area
 *   padding, then the form filling the width, min-height 100dvh.
 * - 601 to 900px: one column; the photo is a 220px banner with the big
 *   tagline only.
 * - from 901px: two equal columns, 12px page padding, the photo sticky at
 *   the viewport height, the form column vertically centred (max 520px).
 *
 * The reference clips the form column (`overflow: hidden`, "fit one
 * screen"). Ours lets the document grow instead: errors, password rules or
 * a short viewport simply scroll, and the photo stays put because it is
 * sticky. The Google button is never hidden on short phones either; the
 * reference drops it under 620px tall.
 */
export function AuthShell({
	audience,
	children,
	overlay,
	className,
}: {
	audience: Audience
	children: ReactNode
	/**
	 * A popup (AuthModal) over the page. Rendered outside the form column,
	 * whose rise-in animation uses a transform: a `position: fixed` child
	 * inside it would be fixed to the column, not the screen.
	 */
	overlay?: ReactNode
	className?: string
}) {
	const tagline = siteContent.auth.tagline[audience]
	const homeHref = SITE_LINKS.home[audience]
	const logoLabel = siteContent.nav.logoLabel

	return (
		<div
			className={cn(
				'tt-site tt-auth flex min-h-dvh flex-col bg-tt-page px-5 min-[601px]:grid min-[601px]:grid-cols-1 min-[601px]:max-[900px]:content-start min-[601px]:gap-0 min-[601px]:p-2.5 min-[901px]:min-h-screen min-[901px]:grid-cols-2 min-[901px]:gap-[clamp(32px,5vw,90px)] min-[901px]:p-3',
				className,
			)}
		>
			{/* Phone header: the ink wordmark only. */}
			<div className="tt-auth-top flex-none pt-[calc(env(safe-area-inset-top,0px)+28px)] min-[601px]:hidden">
				<Link
					to={homeHref}
					aria-label={logoLabel}
					className="inline-block h-[22px] rounded-sm text-tt-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink focus-visible:ring-offset-2"
				>
					<Wordmark title={null} />
				</Link>
			</div>

			{/* Photo panel: a banner on tablets, sticky and full height on desktop. */}
			<aside
				className={cn(
					'relative hidden overflow-hidden bg-[#2a2730] text-white min-[601px]:block min-[601px]:h-[220px] min-[601px]:rounded-[22px] min-[901px]:sticky min-[901px]:top-3 min-[901px]:h-[calc(100vh-24px)] min-[901px]:self-start min-[901px]:rounded-[28px]',
				)}
			>
				{/* Phones never show this panel, so they must not download the
				    photo: the real file is only a <source> for 601px and up, and
				    the <img> itself points at a 1x1 GIF. React does not hoist a
				    preload for an <img> inside <picture>, so nothing in the head
				    fetches it either. Desktop still loads it eagerly, at high
				    priority. */}
				<picture>
					<source media={PHOTO_MEDIA} srcSet={AUTH_IMAGE[audience]} />
					<img
						src={BLANK_PIXEL}
						alt=""
						className={cn(
							'absolute inset-0 h-full w-full object-cover',
							PHOTO_POSITION[audience],
						)}
						fetchPriority="high"
						decoding="async"
					/>
				</picture>
				<div
					aria-hidden="true"
					className={cn(
						'pointer-events-none absolute inset-0',
						PHOTO_SHADE[audience],
					)}
				/>
				<Link
					to={homeHref}
					aria-label={logoLabel}
					className={cn(
						'absolute left-[clamp(24px,3vw,44px)] top-[clamp(24px,3vw,40px)] z-[2] block h-[clamp(28px,2.6vw,38px)] rounded-sm text-white',
						FOCUS_RING,
					)}
				>
					<Wordmark title={null} />
				</Link>
				<div className="absolute bottom-5 left-[clamp(28px,3.4vw,52px)] z-[2] min-[901px]:bottom-[clamp(32px,6vh,64px)]">
					<p className="mb-3 hidden text-[15px] font-medium tracking-[.02em] opacity-90 min-[901px]:block">
						{tagline.eyebrow}
					</p>
					<BigHeading
						as="p"
						className="text-[32px] [text-shadow:0_2px_24px_rgba(0,0,0,.18)] min-[901px]:text-[clamp(44px,5vw,80px)]"
					>
						{tagline.lines.map((line) => (
							<span key={line} className="block">
								{line}
							</span>
						))}
					</BigHeading>
				</div>
			</aside>

			{/* Form column. `my-auto` centres the form when it fits and, unlike
			    `items-center`, keeps the top reachable when it does not. */}
			<div className="tt-auth-col flex flex-1 flex-col pb-[calc(env(safe-area-inset-bottom,0px)+18px)] pt-3.5 min-[601px]:px-2 min-[601px]:pb-9 min-[601px]:pt-7 min-[901px]:min-h-[calc(100vh-24px)] min-[901px]:py-6 min-[901px]:pl-0 min-[901px]:pr-[clamp(0px,3vw,64px)]">
				<div className="tt-auth-in mx-auto my-auto w-full min-[601px]:max-w-[520px] min-[601px]:max-[900px]:mt-4 min-[601px]:max-[900px]:mb-auto min-[901px]:mx-0">
					{children}
				</div>
			</div>
			{overlay}
		</div>
	)
}
